/* eslint-disable max-lines-per-function */

/* eslint-disable complexity */
import _ from 'lodash';
import { InputFieldConfig } from 'src/@vymo/ui/molecules/form/types';
import { Keys, locale } from 'src/i18n';
import { ACTIVITY } from 'src/models/lmsConfig/constants';
import { DISPOSITION_GROUP } from '../../constant';
import { BeforeSaveHookConfigResult } from '../../types';

export const getBeforeSaveHookConfig = (props): BeforeSaveHookConfigResult => {
  if (props?.revyre) {
    return {
      beforeSaveHookConfig: {
        multimedia: {
          isMultimediaBeforeSaveHookDisabled: false,
          uploadUrl: '/vymo/v3/workflow/dms',
          valueFormat: 'documentId',
        },
      },
    };
  }

  return {};
};

export const getActivityDetailsFieldsGrouping = ({
  activityDetailsInputs,
  parentAssociatedWorkflowCompleteInputs,
}: {
  activityDetailsInputs?: InputFieldConfig[];
  parentAssociatedWorkflowCompleteInputs?: InputFieldConfig[];
}) => [
  {
    code: 'engagementDetails',
    name: locale(Keys.ENGAGEMENT_DETAILS),
    fields: (activityDetailsInputs || [])
      .filter((field) => field.code !== DISPOSITION_GROUP)
      .map((field) => field.code),
  },
  {
    code: 'engagementOutcome',
    name: locale(Keys.ENGAGEMENT_OUTCOME),
    fields: (parentAssociatedWorkflowCompleteInputs || [])
      .filter((field) => field.code === DISPOSITION_GROUP)
      .map((field) => field.code),
  },
];

export const getActivityEntityInputsAndGroups = ({
  filteredInputs = [],
  inputsMap,
  revyreConfig,
  parentAssociatedWorkflowCompleteInputs = [],
}: {
  filteredInputs?: InputFieldConfig[];
  inputsMap?;
  revyreConfig?;
  parentAssociatedWorkflowCompleteInputs?: InputFieldConfig[];
}) => {
  const groupedFields = getActivityDetailsFieldsGrouping({
    activityDetailsInputs: filteredInputs,
    parentAssociatedWorkflowCompleteInputs,
  });

  const entityOutcomeGroup = inputsMap?.outcomeGroup;
  const entityWorkflowType = inputsMap?.workflowType;
  const entityWorkflowConfig = revyreConfig?.[entityWorkflowType];
  const events = entityWorkflowConfig?.events;

  const {
    detailInputs = [],
    inputs: eventFallbackInputs = [],
    groups: eventGroups = [],
  } = events?.find((event) => event.allowedAction === entityOutcomeGroup)
    ?.config ?? {};

  const outcomeGroupFieldConfig =
    parentAssociatedWorkflowCompleteInputs?.filter(
      (field) => field.code === DISPOSITION_GROUP,
    );

  return {
    filteredInputs: _.uniqBy(
      filteredInputs.concat(
        detailInputs || eventFallbackInputs,
        outcomeGroupFieldConfig,
      ),
      'code',
    ),
    groups: groupedFields.concat(eventGroups),
  };
};

export const filterInputsWithValues = (
  inputs: InputFieldConfig[] = [],
  formValues = {},
) =>
  inputs.filter((input) => {
    const value = formValues[input.code]?.value;

    if (value == null || value === '') return false;

    if (_.isObject(value)) return !_.isEmpty(value);

    return true;
  });

// Builds form value entries for the final visible input list using entity values.
export const syncFormValuesWithInputs = (
  inputs: InputFieldConfig[] = [],
  inputsMap = {},
  formValues = {},
) => {
  inputs.forEach((input) => {
    formValues[input.code] = { value: inputsMap?.[input?.code] };
  });
};

export const getEntityInputsAndGroups = (props, details, revyreConfig) => {
  if (!details) return { inputs: [], groups: [] };

  if (props.revyre) {
    const { workflowType, domainType } = props;
    const { inputsMap } = details;
    const domainConfig = revyreConfig?.[domainType];
    const workflowConfig = revyreConfig?.[workflowType];
    const formValues = {};
    const profileInputsMap = {};

    const { createConfig } = domainConfig ?? {};
    let { detailsConfig } = domainConfig ?? workflowConfig ?? {};

    const { activityType, workflowType: parentAssociatedWorkflow } =
      inputsMap || {};

    if (parentAssociatedWorkflow && activityType) {
      detailsConfig = {
        inputs: revyreConfig?.[parentAssociatedWorkflow]?.activityConfig?.find(
          (config) => config.type === activityType,
        )?.detail_inputs,
        groups: [],
      };
    }
    const { inputs = [] } = detailsConfig ?? createConfig ?? { inputs: [] };
    let { groups = [] } = detailsConfig ?? createConfig ?? { groups: [] };

    const parentAssociatedWorkflowCompleteInputs = revyreConfig?.[
      parentAssociatedWorkflow
    ]?.activityConfig?.find(
      (config) => config.type === activityType,
    )?.complete_inputs;

    const outcomeGroupFieldConfig =
      parentAssociatedWorkflowCompleteInputs?.find(
        (field) => field.code === DISPOSITION_GROUP,
      );

    let filteredInputs = _.filter(
      _.compact([...inputs, outcomeGroupFieldConfig]),
      (input) => !_.isNil(inputsMap?.[input?.code]),
    );

    if (domainType === ACTIVITY) {
      ({ filteredInputs, groups } = getActivityEntityInputsAndGroups({
        filteredInputs,
        inputsMap,
        revyreConfig,
        parentAssociatedWorkflowCompleteInputs,
      }));
    }

    const { profiles = [] } = details ?? {};

    const profileInputs = profiles.map((profile, index) => {
      const code = `profile_${index + 1}`;
      const hint = profile?.description || `Profile ${index + 1}`;
      const value = profile?.name || '';
      profileInputsMap[code] = value;
      return {
        code,
        default_value: null,
        hidden: false,
        hint,
        oifOptions: null,
        placeholder: '',
        read_only: false,
        required: true,
        source: undefined,
        type: 'text',
        value,
      };
    });

    if (profileInputs?.length) {
      filteredInputs = [...filteredInputs, ...profileInputs];
      groups = [
        {
          code: 'profileGroup',
          name: 'Profiles',
          fields: profileInputs.map((inp) => inp.code),
        },
        ...(groups || []),
      ];
    }

    syncFormValuesWithInputs(
      filteredInputs,
      { ...(inputsMap || {}), ...profileInputsMap },
      formValues,
    );

    const filteredInputsWithValues = filterInputsWithValues(
      filteredInputs,
      formValues,
    );

    return { inputs: filteredInputsWithValues, groups, formValues };
  }

  return { inputs: [], groups: [], formValues: {} };
};
